import { InputOTP6 } from "@/components/molecules/otp";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthAPI } from "@/features/auth/api/auth";
import { getCaptchaToken } from "@/features/utils/captcha";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import { toast } from "sonner";

type Step = "email" | "password" | "forgot-password-otp" | "reset-password";

/**
 * Where the flow stands, kept in sessionStorage: on a phone, going to the mail
 * app for the code often gets the tab discarded, and coming back reloads the
 * page — with the flow in component state only, the visitor landed on the
 * e-mail step again, their code now useless. Per tab, and dropped after the
 * code's own lifetime or once signed in.
 */
const FLOW_KEY = "login_email_password_flow";
const FLOW_TTL_MS = 15 * 60 * 1000;

type SavedFlow = {
  step: Step;
  email: string;
  emailPreSignedToken: string;
  retryUntil: number;
  savedAt: number;
};

const readSavedFlow = (): SavedFlow | null => {
  try {
    const flow = JSON.parse(
      sessionStorage.getItem(FLOW_KEY) || "null",
    ) as SavedFlow | null;
    if (!flow?.step || Date.now() - flow.savedAt > FLOW_TTL_MS) return null;
    // A reset step without its token cannot go anywhere.
    if (flow.step === "reset-password" && !flow.emailPreSignedToken)
      return null;
    return flow;
  } catch {
    return null;
  }
};

const clearSavedFlow = () => {
  try {
    sessionStorage.removeItem(FLOW_KEY);
  } catch {
    // Storage unavailable: nothing was saved either.
  }
};

export const EmailPasswordChallenge = (props: {
  onSuccess: (passwordToken: string) => void;
  onSignup: (email: string, emailToken: string) => void;
  /** Prefilled address, e.g. the one an invitation was sent to. */
  initialEmail?: string;
}) => {
  const { onSuccess, onSignup } = props;
  const { t } = useTranslation();
  // An invitation names its own address: a flow saved for another one is not it.
  const [saved] = useState(() => {
    const flow = readSavedFlow();
    if (flow && props.initialEmail && flow.email !== props.initialEmail)
      return null;
    return flow;
  });
  const [step, setStep] = useState<Step>(saved?.step || "email");
  const [email, setEmail] = useState(saved?.email || props.initialEmail || "");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [emailPreSignedToken, setEmailPreSignedToken] = useState(
    saved?.emailPreSignedToken || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [retryUntil, setRetryUntil] = useState(saved?.retryUntil || 0);
  const [retryTimeLeft, setRetryTimeLeft] = useState(0);

  // Only the steps past the e-mail are worth bringing back.
  useEffect(() => {
    if (step === "email") {
      clearSavedFlow();
      return;
    }
    try {
      sessionStorage.setItem(
        FLOW_KEY,
        JSON.stringify({
          step,
          email,
          emailPreSignedToken,
          retryUntil,
          savedAt: Date.now(),
        } satisfies SavedFlow),
      );
    } catch {
      // Storage unavailable (private mode): the flow just won't survive a reload.
    }
  }, [step, email, emailPreSignedToken, retryUntil]);

  // Counted from a deadline, so it survives a reload and a throttled background tab.
  useEffect(() => {
    const tick = () =>
      setRetryTimeLeft(
        Math.max(0, Math.ceil((retryUntil - Date.now()) / 1000)),
      );
    tick();
    if (retryUntil <= Date.now()) return;
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [retryUntil]);

  const startRetryTimer = () => setRetryUntil(Date.now() + 60 * 1000);

  const succeed = (passwordToken: string) => {
    clearSavedFlow();
    onSuccess(passwordToken);
  };

  // Submit email - check what auth methods are available
  const handleEmailSubmit = async () => {
    if (!email) return;

    setIsLoading(true);
    try {
      // Check if password authentication is available
      const result = await AuthAPI.passwordCheck({
        email,
      });

      if (result.success) {
        // User exists and has password - go to password entry
        setStep("password");
      } else {
        // User doesn't exist or doesn't have password - send OTP
        await AuthAPI.emailChallenge({
          email,
          captchaToken: await getCaptchaToken("email_challenge"),
        });
        toast.success(t("email.codeSent", { email }));
        setStep("forgot-password-otp");
        startRetryTimer();
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("email.sendFailed");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Submit password for login
  const handlePasswordSubmit = async () => {
    if (!password) return;

    setIsLoading(true);
    try {
      // Try direct password login
      const result = await AuthAPI.passwordLogin({
        email,
        password,
      });

      // Success - pass token to parent
      succeed(result.preSignedToken);
    } catch (error) {
      console.error(error);
      const errorMessage = error instanceof Error ? error.message : "";

      // Check if this is a "user not found" error (new user)
      if (
        (errorMessage.includes("Invalid email or password") &&
          step !== "password") ||
        errorMessage.includes("User not found")
      ) {
        // Could be wrong password OR new user - check by sending email challenge
        try {
          const emailResult = await AuthAPI.emailChallenge({
            email,
            captchaToken: await getCaptchaToken("email_challenge"),
          });
          setStep("forgot-password-otp");

          // If no other factors, user doesn't exist - send to signup
          if (emailResult.otherFactors.length === 0) {
            toast.info(t("password.newUser"));
            startRetryTimer();
            return;
          }
        } catch {
          // Email challenge failed - just show generic error
        }
      }

      // Show error
      toast.error(errorMessage || t("password.invalidPassword"));
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password - send OTP
  const handleForgotPassword = async () => {
    setIsLoading(true);
    try {
      await AuthAPI.emailChallenge({
        email,
        captchaToken: await getCaptchaToken("email_challenge"),
      });

      toast.success(t("email.codeSent", { email }));
      setStep("forgot-password-otp");
      startRetryTimer();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("email.sendFailed");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP and move to password reset
  const handleOtpVerify = async (otp: string) => {
    if (otp.length !== 6) return;

    setIsLoading(true);
    try {
      const result = await AuthAPI.emailVerify({
        email,
        otp,
      });

      if (result.success && result.preSignedToken) {
        setEmailPreSignedToken(result.preSignedToken);

        // Check if this is a new user (no other factors)
        if ((result.otherFactors || []).length === 0) {
          // New user - go to signup
          clearSavedFlow();
          onSignup(email, result.preSignedToken);
        } else {
          // Existing user - show password reset
          toast.success(t("email.verified"));
          setStep("reset-password");
        }
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("otp.invalid");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Set new password
  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error(t("signup.passwordRequirements"));
      return;
    }

    setIsLoading(true);
    try {
      await AuthAPI.passwordSet({
        emailPreSignedToken,
        password: newPassword,
      });

      // Now verify with the new password
      const passwordResult = await AuthAPI.passwordVerify({
        emailPreSignedToken,
        password: newPassword,
      });

      if (passwordResult.success && passwordResult.preSignedToken) {
        toast.success(t("password.resetSuccess"));
        succeed(passwordResult.preSignedToken);
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("password.resetFailed");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    await handleForgotPassword();
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} autoComplete="on">
      {/* Step 1: Email input */}
      {step === "email" && (
        <>
          <CardHeader>
            <CardDescription>{t("email.enterEmail")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              <Label>{t("email.label")}</Label>
              <Input
                type="email"
                required
                name="email"
                autoComplete="email username"
                placeholder={t("email.placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email) {
                    e.preventDefault();
                    handleEmailSubmit();
                  }
                }}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              onClick={handleEmailSubmit}
              disabled={!email || isLoading}
            >
              {t("actions.continue")}
            </Button>
          </CardFooter>
        </>
      )}

      {/* Step 2: Email (read-only) + Password */}
      {step === "password" && (
        <>
          <CardHeader>
            <CardDescription>
              {t("password.enterPassword", { email })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="grid gap-2">
                <Label>{t("email.label")}</Label>
                <Input
                  type="email"
                  name="email"
                  autoComplete="email username"
                  value={email}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="grid gap-2">
                <Label>{t("password.label")}</Label>
                <Input
                  type="password"
                  required
                  name="password"
                  autoComplete="current-password"
                  placeholder={t("password.placeholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && password) {
                      e.preventDefault();
                      handlePasswordSubmit();
                    }
                  }}
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button
              type="submit"
              className="w-full mb-2"
              onClick={handlePasswordSubmit}
              disabled={!password || isLoading}
            >
              {isLoading ? t("actions.loading") : t("actions.signIn")}
            </Button>
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={handleForgotPassword}
              disabled={isLoading}
            >
              {t("password.forgotPassword")}
            </Button>
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={() => setStep("email")}
            >
              {t("email.useAnotherEmail")}
            </Button>
          </CardFooter>
        </>
      )}

      {/* Step 3: Forgot password - OTP verification */}
      {step === "forgot-password-otp" && (
        <>
          <CardHeader>
            <CardDescription>
              {t("email.codeSent", {
                email,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 flex items-center justify-center">
              <Label>{t("otp.label")}</Label>
              <InputOTP6
                required
                onChange={(value) => {
                  if (value.length === 6) {
                    handleOtpVerify(value);
                  }
                }}
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-2">
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={handleResendOtp}
              disabled={retryTimeLeft > 0 || isLoading}
            >
              {retryTimeLeft > 0
                ? t("actions.retryIn", { seconds: retryTimeLeft })
                : t("actions.resendCode")}
            </Button>
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={() => setStep("password")}
            >
              {t("actions.back")}
            </Button>
          </CardFooter>
        </>
      )}

      {/* Step 4: Reset password */}
      {step === "reset-password" && (
        <>
          <CardHeader>
            <CardDescription>{t("password.createNew")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              <Label>{t("password.newPassword")}</Label>
              <Input
                type="password"
                required
                name="new-password"
                autoComplete="new-password"
                placeholder={t("password.placeholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newPassword) {
                    e.preventDefault();
                    handlePasswordReset();
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                {t("signup.passwordRequirements")}
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              onClick={handlePasswordReset}
              disabled={!newPassword || newPassword.length < 8 || isLoading}
            >
              {isLoading ? t("actions.loading") : t("password.setPassword")}
            </Button>
          </CardFooter>
        </>
      )}
    </form>
  );
};
